import React, { useContext, useEffect, useRef, useState } from 'react'

import router from 'next/router'
import dynamic from 'next/dynamic'
import useTranslation from 'next-translate/useTranslation'

import {
  Job,
  LocaleEnum,
  SearchableEnum,
  useRecommendationsLazyQuery,
} from 'generated/apolloComponents'

import { useMe } from '@/context/meContext'
import Breadcrumbs from '@/components/atoms/Breadcrumbs'
import CompanyLogo from '@/components/atoms/CompanyLogo'
import JobCompanyInfo from '@/components/organisms/JobCompanyInfo'
import ApplicationSuccess from '@/components/organisms/ApplicationSuccess'
import { useAnalyticsPush } from '@/context/analyticsContext'
import NoSalaryWithInput from '../atoms/NoSalaryWithInput'

import {
  generateJobBreadcrumbs,
  getContractTypes,
  getDaysRemaining,
  getWordForm,
  getLocationsWithAddresses,
  mapLocations,
} from '@/helpers/utils'
import ApplicationButton from '../molecules/ApplicationButton'
import { ActionType, JobContext } from '@/context/jobContext'
import JobIconData from '../molecules/JobIconData'
import {
  ContractIcon,
  EndTimeIcon,
  ExperienceIcon,
  ExpiredIcon,
  LocationIcon,
  RemoteIcon,
  PeopleIcon,
  VacationIcon,
} from '../atoms/JobBoardIcons/JobBoardIcons'
import JobDescription from '../organisms/JobDescription'
import {
  shouldDisplayCustomJobCover,
  shouldDisplayDefaultJobCover,
  workModes,
} from '@/helpers/jobUtils'
import { useCountry } from '@/context/countryContext'
import JobApplyFooterMobile from '../organisms/JobApplyFooterMobile'

interface IProps {
  job: Job
  defaultLocale: string
}

const JobTemplate = ({ job, defaultLocale }: IProps) => {
  const [minSalaryChange, setMinSalaryChange] = useState(false)
  const { t, lang } = useTranslation()
  const { country } = useCountry()
  const { me } = useMe()
  const { pushEvent } = useAnalyticsPush()
  const { state, dispatch } = useContext(JobContext)
  const [recommendations, setRecommendations] = useState<Job[]>([])
  const [_, setAlreadyApplied] = useState(Boolean)
  const daysNum = getDaysRemaining(job)
  const daysWord = `common:${getWordForm(daysNum, 'day', 'days', 'days')}`
  const countryInBreadcrumbs = defaultLocale === 'en'
  const jobExpired: boolean = new Date(job.endsAt) > new Date()
  const trackedComponentRef = useRef<HTMLDivElement>(null)

  const [applicationSuccess, setApplicationSuccess] = useState<boolean>(false)

  const DynamicJobsRecommendations = dynamic(
    () => import('../molecules/JobsRecommendations'),
    { ssr: false }
  )
  const [fetchRecommendations] = useRecommendationsLazyQuery({
    variables: {
      language: lang as LocaleEnum,
      country: country,
      resourceId: job.id,
      resourceType: SearchableEnum.Job,
      returnType: SearchableEnum.Job,
      perPage: 2,
      page: 1,
    },
    onCompleted: (data) => {
      setRecommendations(
        data.recommendations ? (data.recommendations as Job[]) : []
      )
    },
  })

  useEffect(() => {
    const getRecommendations = async () => {
      if (applicationSuccess) {
        setAlreadyApplied(true)
        fetchRecommendations()
        router.push({
          pathname: `/companies/jobs/thank-you/${
            job.internalApplication ? 'i' : 'e'
          }/${job.id}/${job.experienceLevel}/${mapLocations(
            job.locations,
            job.locale
          )}/${job.roles?.join(',')}${
            job.mainTechnology ? `/${job.mainTechnology}` : ''
          }`,
        })
      }
    }
    getRecommendations()
  }, [applicationSuccess])

  useEffect(() => {
    dispatch({ type: ActionType.UpdateJobState, payload: !state.dummyState })
  }, [minSalaryChange])

  useEffect(() => {
    if (window) {
      // @ts-ignore
      pushEvent({
        event: 'viewed',
        jobData: {
          id: job.id,
          location: mapLocations(job.locations, job.locale),
          role: job.roles,
          seniority: job.experienceLevel,
          technology: job.mainTechnology,
          application: job.internalApplication ? 'internal' : 'external',
          company: job.company?.name,
          name: job.position,
        },
      })
    }
  }, [job])

  if (applicationSuccess)
    return <ApplicationSuccess recommendations={recommendations} job={job} />

  return (
    <>
      <style jsx>{`
        .list--check {
          --custom-bg: ${job.company.themeColor || '#726EFF'};
        }
      `}</style>
      <main className='bg-gray-bg '>
        <Breadcrumbs
          data={generateJobBreadcrumbs(job, lang, countryInBreadcrumbs)}
          className='py-6 md:py-10'
        />
        <div className='container pb-14'>
          <div className='flex flex-col md:flex-row'>
            <div className='md:w-2/3 md:pr-1 order-2 md:order-1'>
              <div className='bg-white rounded-lg px-6 py-6 mb-4 hidden md:block'>
                <div className='flex'>
                  <div className='w-1/5'>
                    {job.company.ready && job.company.visible ? (
                      <a
                        href={`/companies/profiles/${job.company.id}`}
                        target='_blank'
                        rel='noreferrer'
                      >
                        <CompanyLogo
                          url={job.company.logo?.url}
                          className='h-12  md:h-28'
                          alt={job.company.name}
                        />
                      </a>
                    ) : (
                      <CompanyLogo
                        url={job.company.logo?.url}
                        className='h-12  md:h-28'
                        alt={job.company.name}
                      />
                    )}
                  </div>
                  <div className='pl-6 w-4/5'>
                    <h2 className='text-c20 leading-6 font-medium text-gray-500'>
                      {job.company.name}
                    </h2>
                    <h1 className='text-c32 font-medium mt-3'>
                      {job.position}
                    </h1>
                  </div>
                </div>
              </div>
              {shouldDisplayCustomJobCover(job) && (
                <div className='bg-white rounded-lg p-1 mb-4 mt-4 md:mt-0'>
                  <img
                    loading='lazy'
                    alt={job.company.name}
                    src={job.topBanner?.url as string}
                    width={job.topBanner?.width || 850}
                    height={job.topBanner?.height || 280}
                    className='rounded-md block object-cover w-full'
                  />
                </div>
              )}
              {shouldDisplayDefaultJobCover(job) &&
                !shouldDisplayCustomJobCover(job) && (
                  <div className='bg-white rounded-lg p-1 mb-4 mt-4 md:mt-0'>
                    <img
                      loading='lazy'
                      alt={job.company.name}
                      src={job.company.jobCover?.url as string}
                      width={850}
                      height={280}
                      className='rounded-md block object-cover w-full'
                    />
                  </div>
                )}
              <JobDescription job={job} />

              <div className='bg-white rounded-lg px-6 md:px-10 py-6 md:py-10'>
                <JobCompanyInfo job={job} />
              </div>
            </div>
            <aside
              className='md:w-1/3 md:pl-3 order-1 md:order-2'
              ref={trackedComponentRef}
            >
              <div className='bg-white rounded-lg px-6 xl:px-20 py-6 mb-4 xl:pb-14 md:mb-0 sticky top-[85px]'>
                <div className='md:hidden'>
                  <p className='mb-1'>{job.company.name}</p>
                  <p className='font-medium text-3xl mb-5'>{job.position}</p>
                </div>
                {jobExpired ? (
                  <div className='mt-8 mb-10 flex'>
                    <ApplicationButton
                      job={job}
                      setApplicationSuccess={setApplicationSuccess}
                      sidePanel
                    />
                  </div>
                ) : (
                  <div className='mt-8 mb-10'>
                    <span className='font-bold text-2xl'>
                      {t('common:expired')}
                    </span>
                    <br />
                    <a
                      href='/companies/jobs'
                      className='text-action hover:text-action-300'
                    >
                      {t('common:expired_link')}
                    </a>
                  </div>
                )}
                {job.showSalary && job.b2bSalary && (
                  <div className='mb-4'>
                    <p className='text-c22 xl:text-2xl'>
                      {`${job.b2bSalary?.money} ${job.b2bSalary?.currency}`}
                    </p>
                    <p className='text-gray-300 xl:text-c22 font-normal mt-1'>
                      + VAT ({t('common:contract_b2b')}){' '}
                      {job.b2bSalary?.timeframe
                        ? `/ ${t(`common:${job.b2bSalary?.timeframe}`)}`
                        : ''}
                    </p>
                  </div>
                )}
                {job.showSalary && job.employmentSalary && (
                  <div className='mb-4'>
                    <p className='text-c22 xl:text-2xl'>
                      {`${job.employmentSalary?.money} ${job.employmentSalary?.currency}`}
                    </p>
                    <p className='text-gray-300 xl:text-c22 font-normal mt-1'>
                      {t('common:gross')} ({t('common:contract_emp')}){' '}
                      {job.employmentSalary?.timeframe
                        ? `/ ${t(`common:${job.employmentSalary?.timeframe}`)}`
                        : ''}
                    </p>
                  </div>
                )}
                {job.showSalary && job.otherSalary && (
                  <div className='mb-4'>
                    <p className='text-c22 xl:text-2xl'>
                      {`${job.otherSalary?.money} ${job.otherSalary?.currency}`}
                    </p>
                    <p className='text-gray-300 xl:text-c22 font-normal mt-1'>
                      {t('common:gross')} ({t('common:contract_other')}){' '}
                      {job.otherSalary?.timeframe
                        ? `/ ${t(`common:${job.otherSalary?.timeframe}`)}`
                        : ''}
                    </p>
                  </div>
                )}
                <NoSalaryWithInput job={job} className='text-c22' />
                {jobExpired ? (
                  <JobIconData
                    icon={<EndTimeIcon />}
                    title={`${t('common:valid_for')} ${daysNum} ${t(daysWord)}`}
                    data={[
                      {
                        data: new Date(job.endsAt).toLocaleDateString('pl-PL'),
                      },
                    ]}
                  />
                ) : (
                  <JobIconData
                    icon={<ExpiredIcon />}
                    title={t('common:expired')}
                    data={[
                      {
                        data: new Date(job.endsAt).toLocaleDateString('pl-PL'),
                      },
                    ]}
                  />
                )}
                <JobIconData
                  icon={<ExperienceIcon />}
                  title={t('common:experience')}
                  data={[
                    {
                      data: t(`common:${job.experienceLevel}`),
                      subData: job.minExperienceInYears
                        ? t('common:min_experience_in_years', {
                            years: job.minExperienceInYears,
                          })
                        : undefined,
                    },
                  ]}
                />
                <JobIconData
                  icon={<PeopleIcon />}
                  title={t('common:employment_type')}
                  data={[{ data: t(`common:${job.employmentType}`) }]}
                />
                <JobIconData
                  icon={<ContractIcon />}
                  title={t('common:contact_type')}
                  data={[
                    {
                      data: getContractTypes(job)
                        .map((x) => t(`${x}`))
                        .join(', '),
                    },
                  ]}
                />
                {job.environment.paidVacations && (
                  <JobIconData
                    icon={<VacationIcon />}
                    title={t('common:paid_leave')}
                    data={[{ data: job.environment.paidVacations }]}
                  />
                )}
                {Number(job.workModes?.length) > 0 && (
                  <JobIconData
                    icon={<RemoteIcon />}
                    title={t('jobs:work_mode')}
                    data={workModes(job)}
                  />
                )}

                {job.locations && job.locations.length > 0 && (
                  <JobIconData
                    icon={<LocationIcon />}
                    title={t('common:localization')}
                    data={getLocationsWithAddresses(
                      job,
                      lang === 'pl' ? 'cityPl' : 'cityEn'
                    )}
                  />
                )}
              </div>
            </aside>
          </div>
        </div>
        <JobApplyFooterMobile
          job={job}
          setApplicationSuccess={setApplicationSuccess}
          triggerComponentRef={trackedComponentRef}
        />
        <div className='container py-8'>
          <DynamicJobsRecommendations
            job={job}
            locale={lang}
            country={country}
          />
        </div>
        {me && me.__typename === 'Admin' && (
          <a
            href={`${process.env.NEXT_PUBLIC_API_URL?.replace(
              '/graphql',
              ''
            )}/admin/jobs/${job.id}`}
            target='_blank'
            rel='noreferrer'
            className='fixed bottom-6 right-6 bg-primary hover:bg-primary-hover text-white px-6 py-3 rounded-full shadow-lg z-50 font-medium'
          >
            🔍
          </a>
        )}
      </main>
    </>
  )
}

export default JobTemplate
